import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag-and-drop quest reordering and nesting controller.
//
// Reorder: drag a quest straight up/down to change its position.
// Nest:    drag a quest to the right (indent) to nest it under the
//          quest row above. A purple left-border highlight shows the
//          target while dragging.
// Un-nest: drag a sub-quest to the left to promote it to top level.
//
// Uses SortableJS for reordering. Nesting calls the reparent endpoint.
//
export default class extends Controller {
  static targets = ["questList", "questRow"]
  static values = {
    moveUrlTemplate: String,
    reparentUrlTemplate: String,
  }

  connect() {
    this.nestTarget = null
    this.dragStartX = 0
    this.currentX = 0
    this.draggedItem = null
    this.initSortable()
  }

  disconnect() {
    if (this.sortable) this.sortable.destroy()
    this.cleanupDragListeners()
  }

  initSortable() {
    if (!this.hasQuestListTarget) return

    this.sortable = Sortable.create(this.questListTarget, {
      handle: "[data-drag-handle]",
      animation: 150,
      ghostClass: "quest-drag-ghost",
      chosenClass: "quest-drag-chosen",
      filter: "[data-task-row]",
      preventOnFilter: false,
      onStart: (evt) => this.onSortStart(evt),
      onEnd: (evt) => this.onSortEnd(evt),
    })
  }

  // --- Drag lifecycle --------------------------------------------------

  onSortStart(evt) {
    this.dragStartX = evt.originalEvent?.clientX || 0
    this.currentX = this.dragStartX
    this.draggedItem = evt.item
    this.nestTarget = null

    this._onPointerMove = (e) => {
      this.currentX = e.clientX
      const offset = this.currentX - this.dragStartX

      this.clearNestHighlight()

      if (offset > 80) {
        const target = this.findNestTargetAtY(e.clientY)
        if (target) {
          this.nestTarget = target
          target.classList.add("quest-nest-target")
        }
      } else {
        this.nestTarget = null
      }
    }
    document.addEventListener("pointermove", this._onPointerMove)
  }

  onSortEnd(evt) {
    this.cleanupDragListeners()
    this.clearNestHighlight()

    const item = evt.item
    const questId = item.dataset.questId
    if (!questId) return

    const offset = this.currentX - this.dragStartX
    const isSubQuest = item.dataset.isSubQuest === "true"

    // Nest: dragged right with a valid target highlighted
    if (offset > 80 && this.nestTarget) {
      const parentId = this.nestTarget.dataset.questId
      this.nestTarget = null
      this.reparentQuest(questId, parentId)
      return
    }

    // Un-nest: sub-quest dragged left → promote to top level
    if (isSubQuest && offset < -80) {
      this.nestTarget = null
      this.reparentQuest(questId, null)
      return
    }

    this.nestTarget = null

    // Sub-quest dragged without enough offset — reload to reset DOM
    if (isSubQuest) {
      window.location.reload()
      return
    }

    // Reorder: top-level quest moved without nesting intent
    const allRows = Array.from(this.questListTarget.children)
    const dropIndex = allRows.indexOf(item)
    let position = 0
    for (let i = 0; i <= dropIndex; i++) {
      const row = allRows[i]
      if (
        !row.hasAttribute("data-sub-quest-row") &&
        !row.hasAttribute("data-task-row")
      ) {
        position++
      }
    }

    this.moveQuest(questId, position)
  }

  // --- Nest target detection -------------------------------------------

  findNestTargetAtY(y) {
    for (const row of this.questRowTargets) {
      if (row === this.draggedItem) continue
      if (row.classList.contains("hidden")) continue
      // Can only nest under top-level quests
      if (row.dataset.isSubQuest === "true") continue

      const rect = row.getBoundingClientRect()
      if (y >= rect.top && y <= rect.bottom) {
        return row
      }
    }
    return null
  }

  clearNestHighlight() {
    this.questListTarget
      .querySelectorAll(".quest-nest-target")
      .forEach((el) => el.classList.remove("quest-nest-target"))
  }

  cleanupDragListeners() {
    if (this._onPointerMove) {
      document.removeEventListener("pointermove", this._onPointerMove)
      this._onPointerMove = null
    }
    this.draggedItem = null
  }

  // --- API calls -------------------------------------------------------

  async reparentQuest(questId, parentId) {
    const url = this.reparentUrlTemplateValue.replace(":id", questId)
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]'
    )?.getAttribute("content")

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({ parent_id: parentId }),
      })

      if (response.ok) {
        const data = await response.json().catch(() => ({}))
        this.showToast(data.message || "Quest updated", "success")
      } else {
        const data = await response.json().catch(() => ({}))
        this.showToast(data.error || "Failed to update quest", "error")
      }
    } catch {
      this.showToast("Network error. Please try again.", "error")
    }

    setTimeout(() => window.location.reload(), 600)
  }

  async moveQuest(questId, position) {
    const url = this.moveUrlTemplateValue.replace(":id", questId)
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]'
    )?.getAttribute("content")

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({ position }),
      })

      if (response.ok) {
        this.showToast("Quest reordered", "success")
      } else {
        this.showToast("Failed to reorder quest", "error")
        setTimeout(() => window.location.reload(), 600)
      }
    } catch {
      this.showToast("Network error. Please try again.", "error")
      setTimeout(() => window.location.reload(), 600)
    }
  }

  // --- Toast -----------------------------------------------------------

  showToast(message, type) {
    const bgColor = type === "success" ? "bg-green-600" : "bg-red-600"
    const toast = document.createElement("div")
    toast.className = `fixed bottom-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-opacity duration-300`
    toast.textContent = message
    document.body.appendChild(toast)

    setTimeout(() => {
      toast.classList.add("opacity-0")
      setTimeout(() => toast.remove(), 300)
    }, 2000)
  }
}
